<template>
  <div class="w-full">
    <!-- Loader -->
    <div v-if="loading" class="qp-card w-full flex items-center justify-center py-16" role="status">
      <Loading />
      <span class="ml-3 text-[14px] text-muted">{{ $t("Loading options data") }}</span>
    </div>

    <!-- Error / empty -->
    <div v-else-if="error" class="w-full qp-card p-8 text-center" role="alert">
      <p class="text-[14px] text-muted">{{ error }}</p>
      <button v-if="canRetry" type="button" class="qp-btn qp-btn-ghost mt-4" @click="load(true)">{{ $t("Retry") }}</button>
    </div>

    <div v-else-if="detail" class="flex flex-col gap-6">
      <!-- OSS header -->
      <div class="qp-card p-4 sm:p-6 flex flex-col lg:flex-row gap-6 lg:gap-10">
        <div class="flex flex-col items-center justify-center min-w-[160px]">
          <span class="qp-label">{{ $t("Option Sweet Spot Score") }}</span>
          <span class="qp-num text-[48px] font-bold leading-none mt-1" :class="scoreClass(detail.oss)">{{ num(detail.oss, 1) }}</span>
        </div>

        <div class="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
          <div v-for="s in subScores" :key="s.key" class="flex flex-col">
            <div class="flex justify-between text-[13px]">
              <span class="text-ink-2">{{ $t(s.label) }} <span class="text-faint">({{ s.weight }}%)</span></span>
              <span class="qp-num font-medium" :class="scoreClass(detail[s.key])">{{ num(detail[s.key], 1) }}</span>
            </div>
            <div class="h-[6px] w-full bg-line-soft rounded-full mt-1">
              <div class="h-[6px] rounded-full" :class="barClass(detail[s.key])"
                :style="{ width: Math.max(0, Math.min(100, Number(detail[s.key]) || 0)) + '%' }"></div>
            </div>
          </div>
        </div>

        <dl class="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-[13px] min-w-[280px] qp-num">
          <dt class="text-muted">IV30</dt><dd>{{ ivPct(detail.iv30) }}</dd>
          <dt class="text-muted" :title="$t('hv30-hint')">HV30</dt><dd>{{ ivPct(detail.hv30) }}</dd>
          <dt class="text-muted" :title="$t('iv-hv-hint')">IV30/HV30</dt><dd>{{ num(detail.ivHvRatio, 2) }}</dd>
          <dt class="text-muted" :title="$t('hv60-hint')">HV60</dt>
          <dd :title="nullTitle(strangleVol.hv60, strangleVol.hv60Reason)">{{ ivPct(strangleVol.hv60) }}</dd>
          <dt class="text-muted" :title="$t('strangle-iv-hint')">{{ $t("Strangle IV") }}</dt>
          <dd :title="nullTitle(strangleVol.strangleIv, strangleVol.strangleReason)">{{ ivPct(strangleVol.strangleIv) }}</dd>
          <dt class="text-muted" :title="$t('strangle-iv-hv60-hint')">{{ $t("Strangle IV / HV60") }}</dt>
          <dd :title="nullTitle(strangleVol.strangleIvHv60, strangleVol.strangleIvHv60Reason)"
            :class="{ 'text-down font-semibold': strangleVol.strangleIvHv60 > strangleIvHvMax }">{{ num(strangleVol.strangleIvHv60, 2) }}</dd>
          <dt class="text-muted" :title="$t('put-call-skew-hint')">{{ $t("Put − Call IV (pts)") }}</dt>
          <dd :title="nullTitle(strangleVol.putCallSkewPts, strangleVol.strangleReason)"
            :class="{ 'text-down font-semibold': strangleVol.putCallSkewPts > putCallSkewMax }">{{ signedPts(strangleVol.putCallSkewPts) }}</dd>
          <dt class="text-muted">{{ $t("IV Percentile") }}</dt><dd>{{ num(detail.ivPercentileTrue, 0) }}</dd>
          <dt class="text-muted">{{ $t("IV Rank") }}</dt><dd>{{ num(detail.ivRank, 0) }}</dd>
          <dt class="text-muted">{{ $t("Next earnings") }}</dt>
          <dd>
            {{ longDate(detail.nextEarningDate) }}
            <span v-if="detail.daysToEarnings != null" class="text-muted">· {{ $t("in {n} days", { n: detail.daysToEarnings }) }}</span>
          </dd>
          <dt class="text-muted">{{ $t("Best expiration") }}</dt>
          <dd>
            <template v-if="hasBestExpiration">
              {{ longDate(detail.bestExpiration) }}
              <span class="text-muted">· {{ detail.bestDte }} {{ $t("DTE") }} · {{ ivPct(detail.bestAtmIv) }}</span>
            </template>
            <template v-else>—</template>
          </dd>
          <dt class="text-muted">{{ $t("Cheap vs History %") }}</dt>
          <dd>
            <span :class="toneClass(detail.cheapVsHistoryPct)">{{ signedPct(detail.cheapVsHistoryPct) }}</span>
            <span class="text-faint"> · {{ sourceLabel(detail.cheapVsHistorySource) }}<span v-if="detail.historyComparables"> · {{ detail.historyComparables }} {{ $t("comparables") }}</span></span>
          </dd>
          <dt class="text-muted">{{ $t("Earnings Cycle") }}</dt>
          <dd class="text-faint">{{ sourceLabel(detail.earningsCycleSource) }}<span v-if="detail.earningsComparables"> · {{ detail.earningsComparables }} {{ $t("comparables") }}</span></dd>
          <dt class="text-muted">{{ $t("Skew Direction") }}</dt><dd>{{ detail.skewDirection ? $t(detail.skewDirection) : "—" }}</dd>
          <dt class="text-muted">{{ $t("Best Contract") }}</dt>
          <dd>
            <template v-if="topContract">
              <span class="font-medium">{{ contractName(topContract) }}</span>
              <span class="ml-1.5 align-middle" :class="typePill(topContract.type)">{{ $t(typeLabel(topContract.type)) }}</span>
            </template>
            <template v-else>{{ $t("No qualifying contract") }}</template>
          </dd>
        </dl>
      </div>

      <!-- Prices are from the last completed session's chain. -->
      <p class="-mt-3 flex items-start gap-2 text-[12px] text-muted">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" class="shrink-0 mt-px" aria-hidden="true">
          <circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.3" />
          <path d="M8 4.5V8l2.2 1.4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" />
        </svg>
        <span>
          {{ $t("chain-as-of", { date: longDate(detail.chainDate) }) }}
          <template v-if="isNum(detail.underlyingPrice)">
            {{ $t("chain-underlying", { then: money(detail.underlyingPrice), now: money(underlyingPrice) }) }}
          </template>
        </span>
      </p>

      <!-- Term structure chart -->
      <div class="qp-card p-4 sm:p-6">
        <h3 class="text-[15px] font-semibold tracking-[-0.01em] text-ink mb-1">
          {{ $t("Term structure") }} — {{ $t("ATM IV") }} × {{ $t("DTE") }}
          <span v-if="earningsDte !== null" class="text-[12px] font-normal text-muted">— <span
              class="inline-block text-[10px] leading-[10px] font-semibold text-amber border border-amber rounded-[3px] px-[3px] py-[2px] align-[1px]">E</span>
            {{ $t("Upcoming earnings") }} · {{ shortDate(detail.nextEarningDate) }}</span>
        </h3>
        <p class="text-[12px] text-muted mb-2">{{ $t("term-structure-hint") }}</p>
        <apexchart type="line" height="280" width="100%" :options="termChartOptions" :series="termChartSeries" />
      </div>

      <!-- Term structure table -->
      <div class="qp-card p-4 sm:p-6">
        <h3 class="text-[15px] font-semibold tracking-[-0.01em] text-ink mb-3">{{ $t("Expirations") }}</h3>
        <div class="overflow-x-auto">
          <table class="w-full text-[13px] qp-num">
            <thead>
              <tr class="text-left text-muted border-b border-line-card">
                <th v-for="c in expirationColumns" :key="c.key" scope="col" class="py-2 pr-4 font-medium whitespace-nowrap"
                  :aria-sort="expSort.key === c.key ? (expSort.dir === 1 ? 'ascending' : 'descending') : 'none'">
                  <button type="button" class="inline-flex items-center gap-1 hover:text-ink" @click="sortExpirations(c.key)">
                    {{ c.label ? $t(c.label) : c.text }}
                    <span v-if="expSort.key === c.key" class="text-brand" aria-hidden="true">{{ expSort.dir === 1 ? "↑" : "↓" }}</span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="e in sortedExpirations" :key="e.expiration" class="border-b border-line-soft"
                :class="isBestExpiration(e) ? 'bg-up-bg/70 font-medium' : ''">
                <td class="py-2 pr-4 whitespace-nowrap">
                  {{ shortDate(e.expiration) }}
                  <span v-if="isBestExpiration(e)" class="qp-chip qp-chip-up ml-1.5 !h-5 !text-[11px]">{{ $t("Best") }}</span>
                </td>
                <td class="py-2 pr-4">{{ e.dte }}</td>
                <td class="py-2 pr-4">{{ money(e.atmStrike) }}</td>
                <td class="py-2 pr-4">{{ ivPct(e.atmIvMean) }}</td>
                <td class="py-2 pr-4" :class="relIvClass(e)">{{ signedPct(relIv(e)) }}</td>
                <td class="py-2 pr-4">{{ ratioPct(e.atmSpreadPct) }}</td>
                <td class="py-2 pr-4">{{ int(e.atmOpenInterest) }}</td>
                <td class="py-2 pr-4">{{ int(e.atmVolume) }}</td>
                <td class="py-2 whitespace-nowrap">
                  <span v-if="e.isValley" class="text-up">▼ {{ ratioPct(e.valleyDepth) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Best contracts -->
      <div class="qp-card p-4 sm:p-6">
        <h3 class="text-[15px] font-semibold tracking-[-0.01em] text-ink mb-3">{{ $t("Best contracts") }}</h3>
        <div v-if="!contracts.length" class="text-[13px] text-muted">{{ $t("No qualifying contract") }}</div>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-[13px] qp-num">
            <thead>
              <tr class="text-left text-muted border-b border-line-card">
                <th scope="col" class="py-2 pr-4 font-medium">{{ $t("Contract") }}</th>
                <th scope="col" class="py-2 pr-4 font-medium">{{ $t("DTE") }}</th>
                <th scope="col" class="py-2 pr-4 font-medium">{{ $t("Delta") }}</th>
                <th scope="col" class="py-2 pr-4 font-medium">IV</th>
                <th scope="col" class="py-2 pr-4 font-medium">{{ $t("Bid") }}</th>
                <th scope="col" class="py-2 pr-4 font-medium">{{ $t("Ask") }}</th>
                <th scope="col" class="py-2 pr-4 font-medium">{{ $t("Spread %") }}</th>
                <th scope="col" class="py-2 pr-4 font-medium">{{ $t("Open Interest") }}</th>
                <th scope="col" class="py-2 font-medium">{{ $t("Score") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in contracts" :key="c.optionSymbol" class="border-b border-line-soft">
                <td class="py-2 pr-4 whitespace-nowrap">
                  <div class="flex items-center gap-2">
                    <span :class="typePill(c.type)">{{ $t(typeLabel(c.type)) }}</span>
                    <span class="font-medium text-ink">{{ contractName(c) }}</span>
                  </div>
                  <div class="text-[11px] text-faint font-mono mt-0.5 select-all" :title="$t('OCC symbol')">{{ c.optionSymbol }}</div>
                </td>
                <td class="py-2 pr-4">{{ c.dte }}</td>
                <td class="py-2 pr-4">{{ num(c.delta, 2) }}</td>
                <td class="py-2 pr-4">{{ ivPct(c.iv) }}</td>
                <td class="py-2 pr-4">{{ money(c.bid) }}</td>
                <td class="py-2 pr-4">{{ money(c.ask) }}</td>
                <td class="py-2 pr-4">{{ ratioPct(c.spreadPct) }}</td>
                <td class="py-2 pr-4">{{ int(c.openInterest) }}</td>
                <td class="py-2 font-semibold" :class="scoreClass(c.score)">{{ num(c.score, 1) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Straddle & strangle at the best expiration -->
      <div v-if="strategies.length" class="qp-card p-4 sm:p-6">
        <h3 class="text-[15px] font-semibold tracking-[-0.01em] text-ink mb-1">{{ $t("Straddle & strangle") }}</h3>
        <p class="text-[12px] text-muted mb-4">
          {{ $t("strategies-hint", { date: longDate(detail.chainDate) }) }}
        </p>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section v-for="s in strategies" :key="s.key" class="rounded-xl border border-line-card p-4 min-w-0">
            <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3">
              <h4 class="text-[14px] font-semibold text-ink">{{ $t(s.title) }}</h4>
              <span v-if="s.data" class="text-[12px] text-muted qp-num">
                {{ shortDate(s.data.expiration) }} · {{ s.data.dte }} {{ $t("DTE") }}
              </span>
            </div>
            <p v-if="!s.data" class="text-[13px] text-muted">{{ $t(s.empty) }}</p>
            <template v-else>
              <div class="overflow-x-auto">
                <table class="w-full text-[13px] qp-num">
                  <thead>
                    <tr class="text-left text-muted border-b border-line-card">
                      <th scope="col" class="py-2 pr-3 font-medium">{{ $t("Contract") }}</th>
                      <th scope="col" class="py-2 pr-3 font-medium">{{ $t("Delta") }}</th>
                      <th scope="col" class="py-2 pr-3 font-medium">IV</th>
                      <th scope="col" class="py-2 pr-3 font-medium">{{ $t("Bid") }} / {{ $t("Ask") }}</th>
                      <th scope="col" class="py-2 font-medium">{{ $t("Open Interest") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="leg in [s.data.put, s.data.call]" :key="leg.optionSymbol" class="border-b border-line-soft">
                      <td class="py-2 pr-3 whitespace-nowrap">
                        <div class="flex items-center gap-2">
                          <span :class="typePill(leg.type)">{{ $t(typeLabel(leg.type)) }}</span>
                          <span class="font-medium text-ink">{{ contractName({ ...leg, expiration: s.data.expiration }) }}</span>
                        </div>
                        <div class="text-[11px] text-faint font-mono mt-0.5 select-all" :title="$t('OCC symbol')">{{ leg.optionSymbol }}</div>
                      </td>
                      <td class="py-2 pr-3">{{ num(leg.delta, 2) }}</td>
                      <td class="py-2 pr-3">{{ ivPct(leg.iv) }}</td>
                      <td class="py-2 pr-3 whitespace-nowrap">{{ money(leg.bid) }} / {{ money(leg.ask) }}</td>
                      <td class="py-2">{{ int(leg.openInterest) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <dl class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 mt-2">
                <div v-for="row in strategyRows(s.data)" :key="row.label"
                  class="flex items-baseline justify-between gap-3 py-2 border-b border-line-soft">
                  <dt class="text-[13px] text-muted">{{ row.label }}</dt>
                  <dd class="qp-num text-[13px] font-medium text-ink text-right">
                    {{ row.value }}<span v-if="row.hint" class="block text-[11px] font-normal text-faint">{{ row.hint }}</span>
                  </dd>
                </div>
              </dl>
            </template>
          </section>
        </div>
      </div>

      <!-- IV30 history -->
      <div class="qp-card p-4 sm:p-6">
        <h3 class="text-[15px] font-semibold tracking-[-0.01em] text-ink mb-3">
          {{ $t("IV30 history") }}
          <span class="text-[12px] font-normal text-muted">— <span class="text-brand">●</span> {{ $t("Comparable point") }}
            · <span class="text-amber">E</span> {{ $t("Earnings") }}</span>
        </h3>
        <apexchart type="line" height="280" width="100%" :options="ivChartOptions" :series="ivChartSeries" />
      </div>
    </div>
  </div>
</template>

<script>
import axios from "../../../api/axios.js";
import Loading from "../../loading/Loading.vue";
import { date as formatDate, isNum, DASH } from "@/utils/format";
import { STRANGLE_IV_HV_MAX, PUT_CALL_SKEW_MAX, nullReasonKey } from "@/utils/strangleVol";
import { chartPalette } from "../Charts/theme";

const EXPIRATION_COLUMNS = [
  { key: "expiration", label: "Expiration" },
  { key: "dte", label: "DTE" },
  { key: "atmStrike", label: "ATM Strike" },
  { key: "atmIvMean", label: "ATM IV" },
  { key: "relIv", label: "vs IV30" },
  { key: "atmSpreadPct", label: "Spread %" },
  { key: "atmOpenInterest", label: "Open Interest" },
  { key: "atmVolume", label: "Volume" },
  { key: "valleyDepth", label: "Valley" },
];

// Where a sub-score's reference comes from (translation keys).
const SOURCE_LABELS = {
  "history-dte-earnings": "source-history-dte-earnings",
  "history-dte": "source-history-dte",
  iv30: "IV30",
  cycle: "source-cycle",
  "percentile-fallback": "source-percentile-fallback",
  "no-earnings": "source-no-earnings",
};

export default {
  name: "QuoteOptions",
  components: { Loading },
  props: {
    symbol: { type: String, default: "" },
    // Term-structure detail already fetched by the page (header badge).
    preloaded: { type: Object, default: null },
    // Live price of the underlying, to compare with the chain's close.
    underlyingPrice: { type: Number, default: null },
  },
  data() {
    return {
      loading: true,
      error: null,
      canRetry: false,
      detail: null,
      strangleIvHvMax: STRANGLE_IV_HV_MAX,
      putCallSkewMax: PUT_CALL_SKEW_MAX,
      expirationColumns: EXPIRATION_COLUMNS,
      expSort: { key: "dte", dir: 1 },
      subScores: [
        { key: "ivCheapness", label: "IV Cheapness", weight: 30 },
        { key: "earningsCycle", label: "Earnings Cycle", weight: 25 },
        { key: "dteOpportunity", label: "DTE Opportunity", weight: 20 },
        { key: "termStructure", label: "Term Structure", weight: 10 },
        { key: "liquidity", label: "Liquidity", weight: 10 },
        { key: "skew", label: "Skew", weight: 5 },
      ],
    };
  },
  watch: {
    symbol() {
      this.load();
    },
    preloaded(val) {
      if (val && this.loading && this.matches(val)) {
        this.detail = val;
        this.error = null;
        this.loading = false;
      }
    },
  },
  mounted() {
    this.load();
  },
  computed: {
    locale() {
      return this.$i18n ? this.$i18n.locale : "en";
    },
    contracts() {
      return Array.isArray(this.detail?.bestContracts) ? this.detail.bestContracts : [];
    },
    // Long ATM straddle and ~20-delta strangle at the best expiration; hidden when neither exists
    // (older API responses, or no best expiration).
    strategies() {
      const s = this.detail?.strategies;
      if (!s || (!s.straddle && !s.strangle)) return [];
      return [
        { key: "straddle", title: "Long straddle (ATM)", data: s.straddle, empty: "no-straddle" },
        { key: "strangle", title: "Long strangle (20Δ)", data: s.strangle, empty: "no-strangle" },
      ];
    },
    // HV60, Strangle IV, Strangle IV / HV60 and put-call skew; {} on older API responses (every value shows a dash).
    strangleVol() {
      return this.detail?.strangleVol || {};
    },
    topContract() {
      return this.contracts[0] || null;
    },
    hasBestExpiration() {
      return /^\d{4}-\d{2}-\d{2}/.test(String(this.detail?.bestExpiration || ""));
    },
    sortedExpirations() {
      const list = [...(this.detail?.expirations || [])];
      const { key, dir } = this.expSort;
      const value = (e) => (key === "relIv" ? this.relIv(e) : key === "expiration" ? String(e.expiration) : e[key]);
      return list.sort((a, b) => {
        const va = value(a);
        const vb = value(b);
        if (va === null || va === undefined) return 1;
        if (vb === null || vb === undefined) return -1;
        return va < vb ? -dir : va > vb ? dir : 0;
      });
    },
    termChartSeries() {
      const pts = (this.detail?.expirations || [])
        .filter((e) => isNum(e.atmIvMean) && e.atmIvMean > 0)
        .map((e) => ({ x: e.dte, y: +(e.atmIvMean * 100).toFixed(2) }));
      return [{ name: this.$t("ATM IV"), data: pts }];
    },
    // Calendar days from the chain date to the next report: the same scale as expiration DTE.
    // Null without a known upcoming date or when it falls after the last charted expiration.
    earningsDte() {
      const d = this.detail || {};
      const re = /^\d{4}-\d{2}-\d{2}/;
      if (!re.test(String(d.nextEarningDate || "")) || !re.test(String(d.chainDate || ""))) return null;
      const day = (s) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
      const n = Math.round((day(d.nextEarningDate) - day(d.chainDate)) / 86400000);
      const dtes = (this.termChartSeries[0]?.data || []).map((p) => p.x);
      if (n < 0 || !dtes.length || n > Math.max(...dtes)) return null;
      return n;
    },
    termChartOptions() {
      const p = chartPalette();
      const d = this.detail || {};
      const best = (d.expirations || []).find((e) => this.isBestExpiration(e));
      // Valleys are marked with dots; the best expiration gets the only label so
      // labels never pile up on short-dated expirations.
      const points = (d.expirations || [])
        .filter((e) => e.isValley && e !== best && isNum(e.atmIvMean))
        .map((e) => ({
          x: e.dte,
          y: +(e.atmIvMean * 100).toFixed(2),
          marker: { size: 5, fillColor: "#00AA6F", strokeColor: p.surface, strokeWidth: 2 },
        }));
      if (best && isNum(best.atmIvMean)) {
        points.push({
          x: best.dte,
          y: +(best.atmIvMean * 100).toFixed(2),
          marker: { size: 7, fillColor: "#2563EB", strokeColor: p.surface, strokeWidth: 2 },
          label: {
            text: `${this.$t("Best expiration")} · ${best.dte} ${this.$t("DTE")}`,
            offsetY: -6,
            style: { background: "#2563EB", color: "#fff", fontSize: "10px" },
          },
        });
      }
      const yaxis = [];
      if (d.iv30 > 0) {
        yaxis.push({
          y: +(d.iv30 * 100).toFixed(2),
          borderColor: "#94A3B8",
          strokeDashArray: 4,
          label: { text: "IV30", position: "left", textAnchor: "start", style: { background: "#94A3B8", color: "#fff", fontSize: "10px" } },
        });
      }
      if (d.historyReferenceIv > 0 && d.cheapVsHistorySource !== "iv30") {
        yaxis.push({
          y: +(d.historyReferenceIv * 100).toFixed(2),
          borderColor: "#D97706",
          strokeDashArray: 2,
          label: { text: this.$t("Historical reference"), style: { background: "#D97706", color: "#fff", fontSize: "10px" } },
        });
      }
      // Upcoming report: outlined "E" on a dashed orange line, like the price chart's upcoming marker.
      const xaxis = [];
      const eDte = this.earningsDte;
      if (eDte !== null) {
        xaxis.push({
          x: eDte,
          borderColor: "#D97706",
          strokeDashArray: 3,
          label: {
            text: "E",
            orientation: "horizontal",
            borderColor: "#D97706",
            style: { background: p.surface, color: "#D97706", fontSize: "9px", fontWeight: 600 },
          },
        });
      }
      const minDte = Math.min(...(this.termChartSeries[0]?.data || []).map((p) => p.x));
      return {
        // Apex's own default text colour in light mode; the theme's muted text in dark.
        chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit", foreColor: p.isDark ? p.muted : "#373d3f", background: "transparent" },
        stroke: { curve: "straight", width: 2 },
        colors: [p.ink],
        markers: { size: 3, strokeWidth: 0 },
        dataLabels: { enabled: false },
        grid: { borderColor: p.lineSoft, strokeDashArray: 3 },
        xaxis: {
          type: "numeric",
          title: { text: this.$t("Days to expiration") },
          decimalsInFloat: 0,
          tickAmount: 6,
          // Widen the axis when the report comes before the first charted expiration.
          min: eDte !== null && eDte < minDte ? eDte : undefined,
          labels: { formatter: (v) => Math.round(v) },
        },
        yaxis: { title: { text: "IV %" }, decimalsInFloat: 1 },
        annotations: { points, yaxis, xaxis },
        tooltip: {
          x: { formatter: (v) => `${v} ${this.$t("DTE")}` },
          y: { formatter: (v) => `${Number(v).toFixed(1)}%` },
          theme: p.isDark ? "dark" : "light",
        },
        noData: { text: this.$t("no-data") },
      };
    },
    ivChartSeries() {
      const pts = (this.detail?.iv30History || [])
        .filter((p) => isNum(p.iv) && p.iv > 0)
        .map((p) => ({ x: new Date(p.date).getTime(), y: +(p.iv * 100).toFixed(2) }));
      return [{ name: "IV30", data: pts }];
    },
    ivChartOptions() {
      const p = chartPalette();
      const d = this.detail || {};
      const comparable = (d.iv30History || []).filter((p) => p.comparable && isNum(p.iv));
      const points = comparable.map((p) => ({
        x: new Date(p.date).getTime(),
        y: +(p.iv * 100).toFixed(2),
        marker: { size: 3, fillColor: "#00AA6F", strokeColor: "#00AA6F", strokeWidth: 0 },
      }));
      const xaxis = (d.pastEarningDates || []).map((e) => ({
        x: new Date(e).getTime(),
        borderColor: "#D97706",
        strokeDashArray: 2,
        label: { text: "E", orientation: "horizontal", style: { background: "#D97706", color: "#fff", fontSize: "9px" } },
      }));
      return {
        // Apex's own default text colour in light mode; the theme's muted text in dark.
        chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false }, fontFamily: "inherit", foreColor: p.isDark ? p.muted : "#373d3f", background: "transparent" },
        stroke: { curve: "smooth", width: 2 },
        colors: [p.ink],
        dataLabels: { enabled: false },
        markers: { size: 0 },
        grid: { borderColor: p.lineSoft, strokeDashArray: 3 },
        xaxis: { type: "datetime", labels: { datetimeUTC: false } },
        yaxis: { title: { text: "IV30 %" }, decimalsInFloat: 1 },
        tooltip: { x: { format: "dd MMM yyyy" }, y: { formatter: (v) => `${Number(v).toFixed(1)}%` }, theme: p.isDark ? "dark" : "light" },
        annotations: { points, xaxis },
        noData: { text: this.$t("no-data") },
      };
    },
  },
  methods: {
    isNum,
    matches(d) {
      return !!d && String(d.symbol || "").toUpperCase() === String(this.symbol || "").toUpperCase();
    },
    async load(force = false) {
      if (!this.symbol) return;
      if (!force && this.matches(this.preloaded)) {
        this.detail = this.preloaded;
        this.error = null;
        this.loading = false;
        return;
      }
      const symbol = this.symbol;
      this.loading = true;
      this.error = null;
      this.canRetry = false;
      this.detail = null;
      try {
        const res = await axios.get("/stock/option-term-structure", { params: { symbol } });
        if (symbol !== this.symbol) return;
        if (res.data?.isError || !res.data?.data) {
          this.error = this.$t("No option chain available for this symbol");
        } else {
          this.detail = res.data.data;
        }
      } catch (e) {
        if (symbol !== this.symbol) return;
        // A 404 means no chain; anything else is worth another try.
        const notFound = e?.response?.status === 404;
        this.error = notFound ? this.$t("No option chain available for this symbol") : this.$t("options-load-failed");
        this.canRetry = !notFound;
      } finally {
        if (symbol === this.symbol) this.loading = false;
      }
    },

    // ---- Formatting: missing values show a dash, never a fake 0 --------------------
    num(v, digits = 2) {
      return isNum(v) ? Number(v).toFixed(digits) : DASH;
    },
    int(v) {
      return isNum(v) ? Math.round(Number(v)).toLocaleString("en-US") : DASH;
    },
    money(v) {
      return isNum(v) ? `$${Number(v).toFixed(2)}` : DASH;
    },
    signedMoney(v) {
      if (!isNum(v)) return DASH;
      const n = Number(v);
      return `${n < 0 ? "−" : "+"}$${Math.abs(n).toFixed(2)}`;
    },
    // Summary rows for one straddle/strangle; prices are per share, one contract = 100 shares.
    strategyRows(s) {
      const perContract = isNum(s.mid) ? `$${Math.round(Number(s.mid) * 100).toLocaleString("en-US")}` : DASH;
      return [
        { label: this.$t("Cost (mid)"), value: this.money(s.mid), hint: this.$t("{amount} per contract", { amount: perContract }) },
        { label: this.$t("Buy at ask"), value: this.money(s.ask), hint: `${this.$t("Sell at bid")} ${this.money(s.bid)}` },
        { label: this.$t("Lower breakeven"), value: this.money(s.breakevenLow), hint: this.signedPct(s.breakevenLowPct) },
        { label: this.$t("Upper breakeven"), value: this.money(s.breakevenHigh), hint: this.signedPct(s.breakevenHighPct) },
        { label: this.$t("Net delta"), value: isNum(s.netDelta) ? `${s.netDelta > 0 ? "+" : ""}${Number(s.netDelta).toFixed(2)}` : DASH },
        { label: this.$t("Theta / day"), value: this.signedMoney(s.thetaPerDay), hint: this.$t("per share") },
        { label: this.$t("Vega / IV point"), value: this.signedMoney(s.vegaPerPoint), hint: this.$t("per share") },
      ];
    },
    // IV fractions (0.2359 -> 23.6%); 0 means the provider had no IV.
    ivPct(v) {
      return isNum(v) && Number(v) > 0 ? `${(Number(v) * 100).toFixed(1)}%` : DASH;
    },
    // Other fractions (spread, valley depth).
    ratioPct(v) {
      return isNum(v) ? `${(Number(v) * 100).toFixed(1)}%` : DASH;
    },
    // Vol points (put − call IV): +7.5 pts.
    signedPts(v) {
      if (!isNum(v)) return DASH;
      const n = Number(v);
      return `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(1)} ${this.$t("pts")}`;
    },
    // Tooltip of a dash: why the value is missing.
    nullTitle(v, reason) {
      return isNum(v) ? null : this.$t(nullReasonKey(reason));
    },
    signedPct(v) {
      if (!isNum(v)) return DASH;
      const n = Number(v);
      return `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
    },
    toneClass(v) {
      if (!isNum(v) || Number(v) === 0) return "text-muted";
      return Number(v) < 0 ? "text-down" : "text-up";
    },
    shortDate(v) {
      const d = String(v || "").slice(0, 10);
      const sameYear = d.slice(0, 4) === String(new Date().getFullYear());
      return formatDate(d, this.locale, sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
    },
    longDate(v) {
      return /^\d{4}-\d{2}-\d{2}/.test(String(v || "")) ? formatDate(String(v).slice(0, 10), this.locale) : DASH;
    },

    // ---- Term structure -------------------------------------------------------------
    // ATM IV relative to IV30, in percent; null when either is missing.
    relIv(e) {
      const iv30 = Number(this.detail?.iv30);
      const iv = Number(e.atmIvMean);
      if (!(iv30 > 0) || !(iv > 0)) return null;
      return ((iv - iv30) / iv30) * 100;
    },
    // Cheaper than IV30 is good for a buyer: green; richer: red.
    relIvClass(e) {
      const v = this.relIv(e);
      if (v === null || v === 0) return "text-muted";
      return v > 0 ? "text-down" : "text-up";
    },
    isBestExpiration(e) {
      return this.hasBestExpiration && String(e.expiration).slice(0, 10) === String(this.detail.bestExpiration).slice(0, 10);
    },
    sortExpirations(key) {
      this.expSort = this.expSort.key === key ? { key, dir: -this.expSort.dir } : { key, dir: 1 };
    },

    // ---- Contracts ------------------------------------------------------------------------
    typeLabel(type) {
      return String(type).toLowerCase() === "put" ? "Put" : "Call";
    },
    typePill(type) {
      const put = String(type).toLowerCase() === "put";
      return [
        "inline-flex items-center h-5 px-1.5 rounded-md text-[11px] font-semibold",
        put ? "bg-down-bg text-down-ink" : "bg-up-bg text-up",
      ];
    },
    // "AAPL Oct 16 '26 $340" from the contract fields (the OCC code is shown below it).
    contractName(c) {
      const d = String(c.expiration || "").slice(0, 10);
      const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      const when = m ? `${formatDate(d, this.locale, { month: "short", day: "numeric" })} '${m[1].slice(-2)}` : "";
      const strike = isNum(c.strike) ? `$${Number(c.strike).toLocaleString("en-US", { maximumFractionDigits: 2 })}` : "";
      return [String(this.detail?.symbol || this.symbol).toUpperCase(), when, strike].filter(Boolean).join(" ");
    },

    // ---- Scores ------------------------------------------------------------------------------
    scoreClass(v) {
      const n = Number(v || 0);
      if (n >= 80) return "text-up";
      if (n >= 60) return "text-amber";
      return "text-muted";
    },
    barClass(v) {
      const n = Number(v || 0);
      if (n >= 80) return "bg-brand";
      if (n >= 60) return "bg-amber";
      return "bg-faint";
    },
    sourceLabel(src) {
      return SOURCE_LABELS[src] ? this.$t(SOURCE_LABELS[src]) : src || "";
    },
  },
};
</script>
